import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  StandardDialog,
  StandardDialogContent,
  StandardDialogHeader,
  StandardDialogBody,
  StandardDialogFooter,
  StandardDialogTitle,
  StandardDialogDescription,
} from "@/components/ui/standard-dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDeletePlace, usePlaceIndex, type PlaceDetail } from "@/hooks/usePlaces";
import { toast } from "@/hooks/useToast";

interface DeletePlaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  place: PlaceDetail;
  onDeleted?: () => void;
}

export function DeletePlaceDialog({
  open,
  onOpenChange,
  place,
  onDeleted,
}: DeletePlaceDialogProps) {
  const { t } = useTranslation("places");
  const deletePlace = useDeletePlace();
  const { data: places = [] } = usePlaceIndex();
  const hasChildren = place.children.length > 0;
  const hasSectionChildren = place.children.some(
    (c) => c.premises_level === "section",
  );
  const [mode, setMode] = useState<"reassign" | "delete_children">(
    hasChildren ? "reassign" : "delete_children",
  );
  const [newParentId, setNewParentId] = useState<string | null>(null);

  // Level-compatible new parents: non-sections, not the place itself or its
  // descendants (the DB validates too; this keeps the picker honest).
  const parentOptions = useMemo(() => {
    const excluded = new Set([place.id, ...place.children.map((c) => c.id)]);
    return places.filter(
      (p) => p.premises_level !== "section" && !excluded.has(p.id),
    );
  }, [places, place]);

  const needsParent = mode === "reassign" && hasSectionChildren && !newParentId;

  const handleDelete = async () => {
    try {
      const result = await deletePlace.mutateAsync({
        placeId: place.id,
        mode: hasChildren ? mode : "delete_children",
        newParentId: mode === "reassign" ? newParentId : null,
      });
      toast.success(
        t("delete.success", {
          name: place.name,
          places: result.deleted_places,
        }),
      );
      onOpenChange(false);
      onDeleted?.();
    } catch {
      // onError toasts
    }
  };

  return (
    <StandardDialog open={open} onOpenChange={onOpenChange}>
      <StandardDialogContent size="md">
        <StandardDialogHeader>
          <StandardDialogTitle>{t("delete.title", { name: place.name })}</StandardDialogTitle>
          <StandardDialogDescription>
            {t("delete.cascadeWarning", {
              tenures: place.tenures.length,
              children: place.children.length,
            })}
          </StandardDialogDescription>
        </StandardDialogHeader>
        <StandardDialogBody className="space-y-4">
          {hasChildren && (
            <>
              <RadioGroup
                value={mode}
                onValueChange={(v) => setMode(v as typeof mode)}
                className="space-y-2"
              >
                <div className="flex items-start gap-2 border rounded-lg p-3">
                  <RadioGroupItem value="reassign" id="delete-mode-reassign" />
                  <div>
                    <Label htmlFor="delete-mode-reassign">{t("delete.reassign")}</Label>
                    <p className="text-xs text-muted-foreground">
                      {t("delete.reassignHint")}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2 border rounded-lg p-3">
                  <RadioGroupItem value="delete_children" id="delete-mode-all" />
                  <div>
                    <Label htmlFor="delete-mode-all">{t("delete.deleteChildren")}</Label>
                    <p className="text-xs text-muted-foreground">
                      {t("delete.deleteChildrenHint", { count: place.children.length })}
                    </p>
                  </div>
                </div>
              </RadioGroup>
              {mode === "reassign" && (
                <div className="space-y-1">
                  <Label>{t("delete.newParent")}</Label>
                  <Select
                    value={newParentId ?? undefined}
                    onValueChange={setNewParentId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("delete.newParentPlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {parentOptions.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} ({p.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {hasSectionChildren && (
                    <p className="text-xs text-muted-foreground">
                      {t("delete.sectionNeedsParent")}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </StandardDialogBody>
        <StandardDialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="destructive"
            disabled={deletePlace.isPending || needsParent}
            onClick={() => void handleDelete()}
          >
            {t("delete.confirm")}
          </Button>
        </StandardDialogFooter>
      </StandardDialogContent>
    </StandardDialog>
  );
}
